import { useState } from "react";
import type { Reward, Artist } from "@/mocks/artistData";

interface Props {
  reward: Reward;
  artist?: Artist;
  onBack: () => void;
  onComplete: (claimData: ClaimResult) => void;
}

export interface ClaimResult {
  type: "coupon" | "form" | "voucher" | "access";
  code?: string;
  formData?: Record<string, string>;
}

// ─── Offline Event: 개인정보 폼 ───────────────────────────────────────────────
function OfflineClaimForm({ reward, artist, onSubmit }: {
  reward: Reward;
  artist?: Artist;
  onSubmit: (data: Record<string, string>) => void;
}) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    date: "",
    note: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const dateOptions = (() => {
    if (!reward.deadline) return ["2026.07.01", "2026.07.08", "2026.07.15"];
    return ["2026.07.01", "2026.07.08", "2026.07.15"];
  })();

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = "이름을 입력해주세요";
    if (!form.phone.trim()) e.phone = "연락처를 입력해주세요";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "올바른 이메일을 입력해주세요";
    if (!form.date) e.date = "참석 날짜를 선택해주세요";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    onSubmit(form);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 mb-2">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: `${reward.color}20` }}>
          <i className="ri-map-pin-line text-lg" style={{ color: reward.color }} />
        </div>
        <div>
          <div className="text-white font-bold text-sm">오프라인 이벤트 신청</div>
          <div className="text-white/40 text-xs">아래 정보를 입력하면 확인 이메일이 발송됩니다</div>
        </div>
      </div>

      {/* 이름 */}
      <div>
        <label className="text-white/50 text-xs font-semibold block mb-1.5">실명 *</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          placeholder="홍길동"
          className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none transition-colors ${errors.name ? "border-[#f72585]" : "border-white/10 focus:border-white/30"}`}
        />
        {errors.name && <p className="text-[#f72585] text-xs mt-1">{errors.name}</p>}
      </div>

      {/* 연락처 */}
      <div>
        <label className="text-white/50 text-xs font-semibold block mb-1.5">연락처 *</label>
        <input
          type="tel"
          value={form.phone}
          onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
          placeholder="010-0000-0000"
          className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none transition-colors ${errors.phone ? "border-[#f72585]" : "border-white/10 focus:border-white/30"}`}
        />
        {errors.phone && <p className="text-[#f72585] text-xs mt-1">{errors.phone}</p>}
      </div>

      {/* 이메일 */}
      <div>
        <label className="text-white/50 text-xs font-semibold block mb-1.5">이메일 *</label>
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
          placeholder="example@email.com"
          className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none transition-colors ${errors.email ? "border-[#f72585]" : "border-white/10 focus:border-white/30"}`}
        />
        {errors.email && <p className="text-[#f72585] text-xs mt-1">{errors.email}</p>}
      </div>

      {/* 참석 날짜 */}
      <div>
        <label className="text-white/50 text-xs font-semibold block mb-1.5">참석 희망 날짜 *</label>
        <div className="grid grid-cols-3 gap-2">
          {dateOptions.map((d) => (
            <button
              key={d}
              onClick={() => setForm((p) => ({ ...p, date: d }))}
              className={`py-2.5 rounded-xl border text-xs font-semibold transition-all cursor-pointer ${
                form.date === d
                  ? "border-white/40 text-white"
                  : "bg-white/5 border-white/10 text-white/40 hover:text-white/70"
              }`}
              style={form.date === d ? { background: `${reward.color}20`, borderColor: `${reward.color}60`, color: reward.color } : {}}
            >
              {d}
            </button>
          ))}
        </div>
        {errors.date && <p className="text-[#f72585] text-xs mt-1">{errors.date}</p>}
      </div>

      {/* 요청사항 */}
      <div>
        <label className="text-white/50 text-xs font-semibold block mb-1.5">요청사항 (선택)</label>
        <textarea
          value={form.note}
          onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
          placeholder="특별한 요청사항이 있으면 입력해주세요..."
          rows={2}
          maxLength={200}
          className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-white/30 resize-none"
        />
      </div>

      <button
        onClick={handleSubmit}
        className="w-full py-3.5 rounded-xl font-black text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-[#0a0a0f] hover:opacity-90"
        style={{ background: reward.color }}
      >
        <i className="ri-send-plane-line" />
        신청 완료하기
      </button>
    </div>
  );
}

// ─── Credit: 크레딧 이름 입력 ─────────────────────────────────────────────────
function CreditClaimForm({ reward, onSubmit }: {
  reward: Reward;
  onSubmit: (data: Record<string, string>) => void;
}) {
  const [form, setForm] = useState({ creditName: "", email: "", sns: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = () => {
    const e: Record<string, string> = {};
    if (!form.creditName.trim()) e.creditName = "크레딧에 올릴 이름을 입력해주세요";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "올바른 이메일을 입력해주세요";
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    onSubmit(form);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 mb-2">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: `${reward.color}20` }}>
          <i className="ri-film-line text-lg" style={{ color: reward.color }} />
        </div>
        <div>
          <div className="text-white font-bold text-sm">크레딧 등재 신청</div>
          <div className="text-white/40 text-xs">크레딧에 올릴 이름과 연락처를 입력해주세요</div>
        </div>
      </div>

      <div className="p-4 rounded-xl bg-[#ffd60a]/10 border border-[#ffd60a]/20">
        <div className="flex items-start gap-2">
          <i className="ri-information-line text-[#ffd60a] text-sm mt-0.5 flex-shrink-0" />
          <p className="text-[#ffd60a]/80 text-xs leading-relaxed">
            입력하신 이름이 실제 크레딧에 그대로 표기됩니다. 신중하게 입력해주세요. 등재 후 변경이 불가합니다.
          </p>
        </div>
      </div>

      <div>
        <label className="text-white/50 text-xs font-semibold block mb-1.5">크레딧 표기 이름 *</label>
        <input
          type="text"
          value={form.creditName}
          onChange={(e) => setForm((p) => ({ ...p, creditName: e.target.value }))}
          placeholder="예: Kim Minjee / 김민지 / MJ_Fan"
          className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none transition-colors ${errors.creditName ? "border-[#f72585]" : "border-white/10 focus:border-white/30"}`}
        />
        {errors.creditName && <p className="text-[#f72585] text-xs mt-1">{errors.creditName}</p>}
        <p className="text-white/25 text-xs mt-1">영문, 한글, 닉네임 모두 가능합니다</p>
      </div>

      <div>
        <label className="text-white/50 text-xs font-semibold block mb-1.5">이메일 (증명서 수령) *</label>
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
          placeholder="example@email.com"
          className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none transition-colors ${errors.email ? "border-[#f72585]" : "border-white/10 focus:border-white/30"}`}
        />
        {errors.email && <p className="text-[#f72585] text-xs mt-1">{errors.email}</p>}
      </div>

      <div>
        <label className="text-white/50 text-xs font-semibold block mb-1.5">SNS 계정 (선택)</label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm">@</span>
          <input
            type="text"
            value={form.sns}
            onChange={(e) => setForm((p) => ({ ...p, sns: e.target.value }))}
            placeholder="인스타그램 또는 X 계정"
            className="w-full pl-8 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/20 focus:outline-none focus:border-white/30"
          />
        </div>
      </div>

      <button
        onClick={handleSubmit}
        className="w-full py-3.5 rounded-xl font-black text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-[#0a0a0f] hover:opacity-90"
        style={{ background: reward.color }}
      >
        <i className="ri-check-double-line" />
        크레딧 등재 신청
      </button>
    </div>
  );
}

// ─── Digital: 쿠폰 코드 발행 ──────────────────────────────────────────────────
function DigitalCouponClaim({ reward, onSubmit }: {
  reward: Reward;
  onSubmit: (data: Record<string, string>) => void;
}) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const handleSubmit = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("올바른 이메일을 입력해주세요");
      return;
    }
    setEmailError("");
    onSubmit({ email });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 mb-2">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: `${reward.color}20` }}>
          <i className="ri-coupon-3-line text-lg" style={{ color: reward.color }} />
        </div>
        <div>
          <div className="text-white font-bold text-sm">디지털 굿즈 쿠폰 발행</div>
          <div className="text-white/40 text-xs">이메일로 다운로드 쿠폰 코드가 발송됩니다</div>
        </div>
      </div>

      {/* 포함 굿즈 미리보기 */}
      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
        <div className="text-white/50 text-xs font-semibold mb-3">포함 굿즈</div>
        <div className="space-y-2">
          {reward.perks.map((perk, i) => (
            <div key={i} className="flex items-center gap-2">
              <i className="ri-download-cloud-line text-xs flex-shrink-0" style={{ color: reward.color }} />
              <span className="text-white/70 text-xs">{perk}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="text-white/50 text-xs font-semibold block mb-1.5">쿠폰 수령 이메일 *</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="example@email.com"
          className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none transition-colors ${emailError ? "border-[#f72585]" : "border-white/10 focus:border-white/30"}`}
        />
        {emailError && <p className="text-[#f72585] text-xs mt-1">{emailError}</p>}
        <p className="text-white/25 text-xs mt-1">쿠폰 코드와 다운로드 링크가 이메일로 발송됩니다</p>
      </div>

      <button
        onClick={handleSubmit}
        className="w-full py-3.5 rounded-xl font-black text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-[#0a0a0f] hover:opacity-90"
        style={{ background: reward.color }}
      >
        <i className="ri-coupon-3-line" />
        쿠폰 발행하기
      </button>
    </div>
  );
}

// ─── Exclusive: 바우처 / 접근 코드 발행 ──────────────────────────────────────
function ExclusiveVoucherClaim({ reward, onSubmit }: {
  reward: Reward;
  onSubmit: (data: Record<string, string>) => void;
}) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const handleSubmit = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("올바른 이메일을 입력해주세요");
      return;
    }
    setEmailError("");
    onSubmit({ email });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 border border-white/10 mb-2">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: `${reward.color}20` }}>
          <i className="ri-key-2-line text-lg" style={{ color: reward.color }} />
        </div>
        <div>
          <div className="text-white font-bold text-sm">독점 콘텐츠 바우처 발행</div>
          <div className="text-white/40 text-xs">이메일로 접근 코드가 발송됩니다</div>
        </div>
      </div>

      {/* 접근 기간 안내 */}
      <div className="grid grid-cols-2 gap-3">
        <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center">
          <i className="ri-time-line text-sm mb-1 block" style={{ color: reward.color }} />
          <div className="text-white font-bold text-sm">30일</div>
          <div className="text-white/40 text-xs">접근 기간</div>
        </div>
        <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-center">
          <i className="ri-device-line text-sm mb-1 block" style={{ color: reward.color }} />
          <div className="text-white font-bold text-sm">3기기</div>
          <div className="text-white/40 text-xs">동시 접속</div>
        </div>
      </div>

      {/* 포함 콘텐츠 */}
      <div className="p-4 rounded-xl bg-white/5 border border-white/10">
        <div className="text-white/50 text-xs font-semibold mb-3">접근 가능 콘텐츠</div>
        <div className="space-y-2">
          {reward.perks.map((perk, i) => (
            <div key={i} className="flex items-center gap-2">
              <i className="ri-lock-unlock-line text-xs flex-shrink-0" style={{ color: reward.color }} />
              <span className="text-white/70 text-xs">{perk}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="text-white/50 text-xs font-semibold block mb-1.5">바우처 수령 이메일 *</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="example@email.com"
          className={`w-full px-4 py-3 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none transition-colors ${emailError ? "border-[#f72585]" : "border-white/10 focus:border-white/30"}`}
        />
        {emailError && <p className="text-[#f72585] text-xs mt-1">{emailError}</p>}
        <p className="text-white/25 text-xs mt-1">발급된 접근 코드는 이메일로 즉시 발송됩니다</p>
      </div>

      <button
        onClick={handleSubmit}
        className="w-full py-3.5 rounded-xl font-black text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-[#0a0a0f] hover:opacity-90"
        style={{ background: reward.color }}
      >
        <i className="ri-key-2-line" />
        바우처 발행하기
      </button>
    </div>
  );
}

// ─── 완료 화면 ────────────────────────────────────────────────────────────────
function ClaimCompleteView({ reward, result, onClose }: {
  reward: Reward;
  result: ClaimResult;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (result.code) {
      navigator.clipboard.writeText(result.code).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getCompleteConfig = () => {
    switch (result.type) {
      case "coupon":
        return {
          icon: "ri-coupon-3-line",
          title: "쿠폰 발행 완료!",
          desc: "이메일로 다운로드 쿠폰 코드가 발송되었습니다.",
          showCode: true,
        };
      case "voucher":
        return {
          icon: "ri-key-2-line",
          title: "바우처 발행 완료!",
          desc: "이메일로 독점 콘텐츠 접근 코드가 발송되었습니다.",
          showCode: true,
        };
      case "form":
        return {
          icon: "ri-calendar-check-line",
          title: "신청 완료!",
          desc: "이메일로 확인서가 발송됩니다. 크리에이터 측에서 확정 후 안내 드립니다.",
          showCode: false,
        };
      case "access":
        return {
          icon: "ri-film-line",
          title: "크레딧 등재 신청 완료!",
          desc: "크레딧 등재 신청이 접수되었습니다. 이메일로 공식 증명서가 발송됩니다.",
          showCode: false,
        };
    }
  };

  const config = getCompleteConfig();

  return (
    <div className="p-6 text-center space-y-5">
      <div
        className="w-16 h-16 flex items-center justify-center rounded-2xl mx-auto"
        style={{ background: `${reward.color}20`, border: `1px solid ${reward.color}40` }}
      >
        <i className={`${config.icon} text-2xl`} style={{ color: reward.color }} />
      </div>

      <div>
        <h3 className="text-white font-black text-lg mb-2">{config.title}</h3>
        <p className="text-white/40 text-sm leading-relaxed">{config.desc}</p>
      </div>

      {config.showCode && result.code && (
        <div className="p-4 rounded-xl border" style={{ background: `${reward.color}10`, borderColor: `${reward.color}30` }}>
          <div className="text-white/50 text-xs mb-2">발급된 코드</div>
          <div className="flex items-center gap-3 justify-center">
            <span className="font-mono font-black text-lg tracking-widest" style={{ color: reward.color }}>
              {result.code}
            </span>
            <button
              onClick={handleCopy}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 text-white/60 hover:text-white transition-colors cursor-pointer flex-shrink-0"
            >
              <i className={copied ? "ri-check-line" : "ri-file-copy-line"} />
            </button>
          </div>
          <p className="text-white/25 text-xs mt-2">이메일로도 발송되었습니다</p>
        </div>
      )}

      {result.type === "form" && result.formData && (
        <div className="p-4 rounded-xl bg-white/5 border border-white/10 text-left space-y-2">
          <div className="text-white/50 text-xs font-semibold mb-2">신청 정보</div>
          {result.formData.name && (
            <div className="flex justify-between text-xs">
              <span className="text-white/40">이름</span>
              <span className="text-white/70">{result.formData.name}</span>
            </div>
          )}
          {result.formData.date && (
            <div className="flex justify-between text-xs">
              <span className="text-white/40">참석 날짜</span>
              <span className="text-white/70">{result.formData.date}</span>
            </div>
          )}
          {result.formData.email && (
            <div className="flex justify-between text-xs">
              <span className="text-white/40">이메일</span>
              <span className="text-white/70">{result.formData.email}</span>
            </div>
          )}
        </div>
      )}

      <button
        onClick={onClose}
        className="w-full py-3 rounded-xl font-bold text-sm bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
      >
        닫기
      </button>
    </div>
  );
}

// ─── 메인 컴포넌트 ────────────────────────────────────────────────────────────
function generateCode(prefix: string): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = prefix + "-";
  for (let i = 0; i < 4; i++) code += chars[Math.floor(Math.random() * chars.length)];
  code += "-";
  for (let i = 0; i < 4; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

export default function RewardClaimProcess({ reward, artist, onBack, onComplete }: Props) {
  const [claimResult, setClaimResult] = useState<ClaimResult | null>(null);

  const handleOfflineSubmit = (data: Record<string, string>) => {
    const result: ClaimResult = { type: "form", formData: data };
    setClaimResult(result);
    onComplete(result);
  };

  const handleCreditSubmit = (data: Record<string, string>) => {
    const result: ClaimResult = { type: "access", formData: data };
    setClaimResult(result);
    onComplete(result);
  };

  const handleDigitalSubmit = (data: Record<string, string>) => {
    const code = generateCode("DGT");
    const result: ClaimResult = { type: "coupon", code, formData: data };
    setClaimResult(result);
    onComplete(result);
  };

  const handleExclusiveSubmit = (data: Record<string, string>) => {
    const code = generateCode("EXC");
    const result: ClaimResult = { type: "voucher", code, formData: data };
    setClaimResult(result);
    onComplete(result);
  };

  if (claimResult) {
    return (
      <ClaimCompleteView
        reward={reward}
        result={claimResult}
        onClose={() => onComplete(claimResult)}
      />
    );
  }

  return (
    <div>
      {/* 뒤로가기 헤더 */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-white/10">
        <button
          onClick={onBack}
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-white/50 hover:text-white transition-colors cursor-pointer flex-shrink-0"
        >
          <i className="ri-arrow-left-line text-sm" />
        </button>
        <div>
          <div className="text-white font-bold text-sm">리워드 수령 신청</div>
          <div className="text-white/40 text-xs truncate max-w-xs">{reward.title}</div>
        </div>
        <div
          className="ml-auto px-2.5 py-1 rounded-full text-[10px] font-semibold flex-shrink-0"
          style={{ background: `${reward.color}20`, color: reward.color, border: `1px solid ${reward.color}30` }}
        >
          {reward.categoryLabel}
        </div>
      </div>

      <div className="p-6">
        {reward.category === "offline" && (
          <OfflineClaimForm reward={reward} artist={artist} onSubmit={handleOfflineSubmit} />
        )}
        {reward.category === "credit" && (
          <CreditClaimForm reward={reward} onSubmit={handleCreditSubmit} />
        )}
        {reward.category === "digital" && (
          <DigitalCouponClaim reward={reward} onSubmit={handleDigitalSubmit} />
        )}
        {reward.category === "exclusive" && (
          <ExclusiveVoucherClaim reward={reward} onSubmit={handleExclusiveSubmit} />
        )}
      </div>
    </div>
  );
}
